import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CheckSquare, Plus } from 'lucide-react';
import ProduccionLayout from '@/components/ProduccionLayout';

// Hooks personalizados
import useTareas from '@/hooks/tablero/useTareas';
import useFilters from '@/hooks/tablero/useFilters';

// Componentes
import VistaKanban from '@/components/tablero/VistaKanban';
import VistaLista from '@/components/tablero/VistaLista';
import VistaResponsable from '@/components/tablero/VistaResponsable';
import VistaLote from '@/components/tablero/VistaLote';
import VistaCalendario from '@/components/tablero/VistaCalendario';
import FiltrosAvanzados from '@/components/tablero/FiltrosAvanzados';
import NotificacionesPanel from '@/components/tablero/NotificacionesPanel';
import TareaFormModal from '@/components/tablero/TareaFormModal';
import TareaDetailModal from '@/components/tablero/TareaDetailModal';

// Utils
import { VISTAS, getTareaFormInitial, tareaToFormData } from '@/utils/tareaUtils';
import { CheckSquare as CheckSquareIcon, List, Calendar, User, MapPin } from 'lucide-react';

const VISTAS_ICONS = {
  kanban: CheckSquareIcon,
  lista: List,
  calendario: Calendar,
  responsable: User,
  lote: MapPin,
};

export default function TableroTareasCompleto({ user, onLogout }) {
  // Estados de vista y UI
  const [vistaActual, setVistaActual] = useState('kanban');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [tareaDetalle, setTareaDetalle] = useState(null);
  const [tareaExpandida, setTareaExpandida] = useState(null);

  // Estados para formulario de tarea
  const [tareaForm, setTareaForm] = useState(getTareaFormInitial());
  const [nuevaSubtarea, setNuevaSubtarea] = useState('');
  const [centrosPorLote, setCentrosPorLote] = useState([]);

  // Estados para modal de detalle
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [aplicandoMateriales, setAplicandoMateriales] = useState(false);

  // Hook de tareas
  const {
    tareas,
    trabajadores,
    lotes,
    materiales,
    labores,
    centros,
    notificaciones,
    loading,
    fetchData,
    createTarea,
    updateTarea,
    deleteTarea,
    updateEstadoTarea,
    toggleSubtarea,
    addComentario,
    uploadAdjunto,
    deleteAdjunto,
    aplicarMateriales,
    marcarNotificacionLeida,
    marcarTodasLeidas,
    loadCentrosByLote,
    asignarResponsable,
    asignarRevisor,
  } = useTareas(user);

  // Hook de filtros
  const { filtros, setFiltros, tareasFiltradas, getTareasPorEstado } = useFilters(tareas);

  // Handlers
  const handleOpenDialog = async (tarea = null) => {
    if (tarea) {
      setEditingTarea(tarea);
      setTareaForm(tareaToFormData(tarea));
      
      if (tarea.lote_id) {
        const centros = await loadCentrosByLote(tarea.lote_id);
        setCentrosPorLote(centros);
      }
    } else {
      setEditingTarea(null);
      setTareaForm(getTareaFormInitial());
      setCentrosPorLote([]);
    }
    setDialogOpen(true);
  };

  const handleViewDetail = (tarea) => {
    setTareaDetalle(tarea);
    setDetailDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tareaForm.titulo) {
      return;
    }

    const userName = user.full_name || user.username;
    let success;

    if (editingTarea) {
      success = await updateTarea(editingTarea.id, tareaForm);
    } else {
      success = await createTarea(tareaForm, userName);
    }

    if (success) {
      setDialogOpen(false);
    }
  };

  const handleDelete = async (tareaId) => {
    await deleteTarea(tareaId);
  };

  const handleAgregarSubtarea = () => {
    if (!nuevaSubtarea.trim()) return;

    setTareaForm({
      ...tareaForm,
      subtareas: [
        ...tareaForm.subtareas,
        { titulo: nuevaSubtarea, completada: false, id: `temp-${Date.now()}` }
      ]
    });
    setNuevaSubtarea('');
  };

  const handleEliminarSubtarea = (index) => {
    setTareaForm({
      ...tareaForm,
      subtareas: tareaForm.subtareas.filter((_, i) => i !== index)
    });
  };

  const handleToggleSubtarea = async (tareaId, subtareaId) => {
    const userName = user.full_name || user.username;
    const success = await toggleSubtarea(tareaId, subtareaId, userName);
    
    if (success && tareaDetalle && tareaDetalle.id === tareaId) {
      const updatedTarea = tareas.find(t => t.id === tareaId);
      if (updatedTarea) {
        setTareaDetalle(updatedTarea);
      }
    }
  };

  const handleAgregarComentario = async () => {
    if (!nuevoComentario.trim()) return;

    const userName = user.full_name || user.username;
    const success = await addComentario(tareaDetalle.id, nuevoComentario, userName);
    
    if (success) {
      setNuevoComentario('');
      const updatedTarea = tareas.find(t => t.id === tareaDetalle.id);
      if (updatedTarea) {
        setTareaDetalle(updatedTarea);
      }
    }
  };

  const handleUploadFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    const success = await uploadAdjunto(tareaDetalle.id, file);
    setUploadingFile(false);
    
    if (success) {
      const updatedTarea = tareas.find(t => t.id === tareaDetalle.id);
      if (updatedTarea) {
        setTareaDetalle(updatedTarea);
      }
      event.target.value = '';
    }
  };

  const handleDeleteAdjunto = async (index) => {
    const success = await deleteAdjunto(tareaDetalle.id, index);
    if (success) {
      const updatedTarea = tareas.find(t => t.id === tareaDetalle.id);
      if (updatedTarea) {
        setTareaDetalle(updatedTarea);
      }
    }
  };

  const handleAplicarMateriales = async (tareaId) => {
    setAplicandoMateriales(true);
    const success = await aplicarMateriales(tareaId);
    setAplicandoMateriales(false);
    
    if (success && tareaDetalle && tareaDetalle.id === tareaId) {
      const updatedTarea = tareas.find(t => t.id === tareaId);
      if (updatedTarea) {
        setTareaDetalle(updatedTarea);
      }
    }
  };

  const handleLoteChange = async (loteId) => {
    const lote = lotes.find(l => l.id === loteId);
    setTareaForm({
      ...tareaForm,
      lote_id: loteId,
      lote_nombre: lote?.nombre || '',
      centros: [],
    });

    if (loteId) {
      const centros = await loadCentrosByLote(loteId);
      setCentrosPorLote(centros);
    } else {
      setCentrosPorLote([]);
    }
  };

  if (loading) {
    return (
      <ProduccionLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Cargando...</p>
        </div>
      </ProduccionLayout>
    );
  }

  const userName = user.full_name || user.username;

  return (
    <ProduccionLayout user={user} onLogout={onLogout}>
      <div className="max-w-[98%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <CheckSquare className="w-8 h-8 mr-3 text-emerald-600" />
            <div>
              <h1 className="text-3xl font-bold">Tablero de Tareas</h1>
              <p className="text-sm text-gray-600">Gestiona las tareas de la finca</p>
            </div>
          </div>
          <div className="flex gap-2">
            <NotificacionesPanel
              notificaciones={notificaciones}
              onMarcarLeida={marcarNotificacionLeida}
              onMarcarTodasLeidas={marcarTodasLeidas}
              onViewTarea={handleViewDetail}
              tareas={tareas}
            />
            
            <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tarea
            </Button>
          </div>
        </div>

        {/* Selector de Vista */}
        <div className="flex gap-2 mb-4">
          {VISTAS.map(vista => {
            const Icon = VISTAS_ICONS[vista.id];
            return (
              <Button
                key={vista.id}
                variant={vistaActual === vista.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVistaActual(vista.id)}
                className={vistaActual === vista.id ? 'bg-emerald-600' : ''}
              >
                <Icon className="w-4 h-4 mr-2" />
                {vista.label}
              </Button>
            );
          })}
        </div>

        {/* Filtros */}
        <FiltrosAvanzados
          filtros={filtros}
          setFiltros={setFiltros}
          trabajadores={trabajadores}
          lotes={lotes}
        />

        {/* Vistas */}
        {vistaActual === 'kanban' && (
          <VistaKanban
            tareas={tareasFiltradas}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            onViewDetail={handleViewDetail}
            onUpdateEstado={updateEstadoTarea}
            onAsignarResponsable={asignarResponsable}
            onAsignarRevisor={asignarRevisor}
            tareaExpandida={tareaExpandida}
            onToggleExpand={(tareaId) => setTareaExpandida(tareaExpandida === tareaId ? null : tareaId)}
            userName={userName}
            trabajadores={trabajadores}
          />
        )}

        {vistaActual === 'lista' && (
          <VistaLista
            tareas={tareasFiltradas}
            onEdit={handleOpenDialog}
            onDelete={handleDelete}
            onViewDetail={handleViewDetail}
          />
        )}

        {vistaActual === 'responsable' && (
          <VistaResponsable
            tareas={tareasFiltradas}
            trabajadores={trabajadores}
          />
        )}

        {vistaActual === 'lote' && (
          <VistaLote
            tareas={tareasFiltradas}
            lotes={lotes}
          />
        )}

        {vistaActual === 'calendario' && (
          <VistaCalendario
            tareas={tareasFiltradas}
            onViewDetail={handleViewDetail}
          />
        )}

        {/* Modales */}
        <TareaFormModal
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          tareaForm={tareaForm}
          setTareaForm={setTareaForm}
          editingTarea={editingTarea}
          onSave={handleSave}
          labores={labores}
          lotes={lotes}
          trabajadores={trabajadores}
          centrosPorLote={centrosPorLote}
          onLoteChange={handleLoteChange}
          nuevaSubtarea={nuevaSubtarea}
          setNuevaSubtarea={setNuevaSubtarea}
          onAgregarSubtarea={handleAgregarSubtarea}
          onEliminarSubtarea={handleEliminarSubtarea}
        />

        <TareaDetailModal
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          tarea={tareaDetalle}
          onEdit={handleOpenDialog}
          onToggleSubtarea={handleToggleSubtarea}
          onAgregarComentario={handleAgregarComentario}
          nuevoComentario={nuevoComentario}
          setNuevoComentario={setNuevoComentario}
          onUploadFile={handleUploadFile}
          uploadingFile={uploadingFile}
          onDeleteAdjunto={handleDeleteAdjunto}
          onAplicarMateriales={handleAplicarMateriales}
          aplicandoMateriales={aplicandoMateriales}
        />
      </div>
    </ProduccionLayout>
  );
}
